/**
 * Route records for the geography and tax-pack screens.
 *
 * Exported rather than registered: `src/router/index.ts` is the app's file and
 * this module does not edit it. Spread `geotaxRoutes` into its `routes` array
 * before the catch-all, and add the three nav entries named in
 * `geotax.nav.*` — the orchestrator's brief for this packet reserves both.
 *
 * `titleKey` and `breadcrumb` carry catalogue **keys**, not prose, exactly as
 * the existing records do: route records are built once at module load, long
 * before a locale switch, so a literal here would freeze the document title in
 * whatever language the console booted in.
 *
 * Importing this file registers the module's locale fragments (`./i18n`) as a
 * side effect, which is why it is imported eagerly while the views themselves
 * stay lazy: the router is built before any view loads, and a route title has
 * to resolve at that moment.
 */

import type { RouteRecordRaw } from 'vue-router'

import './i18n'

export const geotaxRoutes: RouteRecordRaw[] = [
  {
    path: '/geography',
    name: 'geography',
    component: () => import('./GeographyView.vue'),
    meta: {
      titleKey: 'geotax.routes.geography',
      breadcrumb: [{ labelKey: 'geotax.routes.geography' }],
    },
  },
  {
    path: '/tax-packs',
    name: 'tax-packs',
    component: () => import('./TaxPacksView.vue'),
    meta: {
      titleKey: 'geotax.routes.taxPacks',
      breadcrumb: [{ labelKey: 'geotax.routes.taxPacks' }],
    },
  },
  {
    path: '/tax-packs/:id',
    name: 'tax-pack-detail',
    component: () => import('./TaxPackDetailView.vue'),
    props: true,
    meta: {
      titleKey: 'geotax.routes.taxPack',
      breadcrumb: [{ labelKey: 'geotax.routes.taxPacks', to: '/tax-packs' }],
    },
  },
  {
    path: '/tax-pack-adoption',
    name: 'tax-pack-adoption',
    component: () => import('./AdoptionView.vue'),
    meta: {
      titleKey: 'geotax.routes.adoption',
      breadcrumb: [
        { labelKey: 'geotax.routes.taxPacks', to: '/tax-packs' },
        { labelKey: 'geotax.routes.adoption' },
      ],
    },
  },
  {
    path: '/tax-packs/tenants/:tenantId',
    name: 'tenant-tax-packs',
    component: () => import('./TenantTaxPacksView.vue'),
    props: true,
    meta: {
      titleKey: 'geotax.routes.tenantTaxPacks',
      breadcrumb: [
        { labelKey: 'geotax.routes.taxPacks', to: '/tax-packs' },
        { labelKey: 'geotax.routes.adoption', to: '/tax-pack-adoption' },
        { labelKey: 'geotax.routes.tenantTaxPacks' },
      ],
    },
  },
]
